/**
 * PosClient — typed HTTP client for tilt-pos-api.
 *
 * Mirrors the AdminClient pattern (TokenStore + ApiError + per-route
 * methods). Two POS-specific niceties:
 *
 *  1. Optional ``getActiveMembershipId`` callback. When provided, the
 *     client stamps ``X-Active-Membership-Id`` on every authenticated
 *     request so the backend resolves permissions against the right
 *     scope without the caller threading the id through every call.
 *  2. ``setActiveMembership`` — POST + return-permissions in one shot,
 *     so the UI can update the gate without a follow-up GET /profile.
 */
import { ApiError } from "./types.js";
export class PosClient {
    baseUrl;
    tokens;
    getActiveMembershipId;
    fetchImpl;
    constructor(opts) {
        this.baseUrl = opts.baseUrl.replace(/\/$/, "");
        this.tokens = opts.tokens;
        if (opts.getActiveMembershipId) {
            this.getActiveMembershipId = opts.getActiveMembershipId;
        }
        // bind to globalThis: storing the bare reference detaches `this`
        // from Window and browsers throw "Illegal invocation" on call.
        this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
    }
    /** GET /health — un-authenticated. */
    async health() {
        return this.request("GET", "/health", { authenticated: false });
    }
    /** GET /pos/v1/public/anchor-by-slug — un-authenticated.
     *  Resolves the URL slug (e.g. ``honda-group``) to the anchor
     *  metadata + brand config so the login page renders branded
     *  before any auth happens. */
    async anchorBySlug(slug) {
        return this.request("GET", `/pos/v1/public/anchor-by-slug?slug=${encodeURIComponent(slug)}`, { authenticated: false });
    }
    /** POST /pos/v1/public/sign-in-init — un-authenticated.
     *  Maps (email, anchor) → the UUID Cognito Username. The login
     *  page calls this before SRP because the same email can repeat
     *  across anchors and we keyed Cognito on UUID. */
    async signInInit(input) {
        return this.request("POST", "/pos/v1/public/sign-in-init", {
            authenticated: false,
            body: input,
        });
    }
    /** GET /pos/v1/profile — full identity for the active membership. */
    async getProfile() {
        return this.request("GET", "/pos/v1/profile");
    }
    /** GET /pos/v1/profile/me — alias of /profile, for SCIM-style URLs. */
    async getMe() {
        return this.request("GET", "/pos/v1/profile/me");
    }
    /** PATCH /pos/v1/profile/me — self-update first/last name + phone. */
    async patchMe(body) {
        return this.request("PATCH", "/pos/v1/profile/me", { body });
    }
    /**
     * POST /pos/v1/profile/active-membership — validate the requested
     * membership belongs to the caller and return the freshly resolved
     * permission set. The client persists the chosen id locally and
     * sends it as ``X-Active-Membership-Id`` on subsequent requests.
     */
    async setActiveMembership(membershipId) {
        return this.request("POST", "/pos/v1/profile/active-membership", {
            body: { membership_id: membershipId },
        });
    }
    /**
     * POST /pos/v1/profile/me/pin — set or change the caller's PIN
     * (#149). When ``has_pin`` is already true on /profile, the body
     * MUST include ``current_pin`` — the server verifies it before
     * accepting the change. Mismatch returns 403 (current_pin_invalid).
     */
    async setPin(body) {
        return this.request("POST", "/pos/v1/profile/me/pin", { body });
    }
    /**
     * DELETE /pos/v1/profile/me/pin — clear the caller's PIN. The
     * body must include ``current_pin``; idempotent if no PIN is set.
     */
    async clearPin(body) {
        return this.request("DELETE", "/pos/v1/profile/me/pin", { body });
    }
    /**
     * POST /pos/v1/profile/me/verify-pin — lock-screen PIN re-auth.
     * Returns 200 on success; throws ApiError(403) on wrong PIN,
     * ApiError(423) when locked out, ApiError(404) when no PIN is set.
     */
    async verifyPin(body) {
        return this.request("POST", "/pos/v1/profile/me/verify-pin", { body });
    }
    /**
     * POST /pos/v1/profile/mfa/setup — begin TOTP enrolment.
     * Returns the secret and a ready-to-scan otpauth:// URI.
     * The client renders a QR code; the user scans and then calls mfaVerify.
     */
    async mfaSetup() {
        return this.request("POST", "/pos/v1/profile/mfa/setup");
    }
    /**
     * POST /pos/v1/profile/mfa/verify — confirm the 6-digit TOTP code.
     * On success activates SOFTWARE_TOKEN_MFA as the preferred method.
     */
    async mfaVerify(body) {
        return this.request("POST", "/pos/v1/profile/mfa/verify", { body });
    }
    // ---------------- Orders + payments (POS-1.0) ---------------- //
    /** POST /pos/v1/orders — open a new payment-intake order. */
    async createOrder(body) {
        return this.request("POST", "/pos/v1/orders", { body });
    }
    /** GET /pos/v1/locations — every location the active membership
     *  can see. Drives the multi-select location-subset picker on
     *  Reports / Dashboard / Transactions for org / partner / platform-
     *  scoped operators. Location-scoped operators get their single
     *  location back so the UI can render a uniform response shape. */
    async listVisibleLocations() {
        return this.request("GET", `/pos/v1/locations`);
    }
    /** GET /pos/v1/locations/{id}/departments — active departments at
     *  this location with the effective ``reference_label`` resolved.
     *  Drives the sale-screen department picker + cues the External
     *  Reference field with the right business term. */
    async listLocationDepartments(locationId) {
        return this.request("GET", `/pos/v1/locations/${encodeURIComponent(locationId)}/departments`);
    }
    /** GET /pos/v1/discount-types — active discount types for the cashier
     *  dropdown in TakePaymentWizard. Partner-scoped; ordered by sort_order. */
    async listDiscountTypes() {
        return this.request("GET", "/pos/v1/discount-types");
    }
    /** GET /pos/v1/customers?q=... — typeahead customer search across
     *  the active scope's pos_customer_tbl. Auto-detects phone / email /
     *  name shape. ``q`` is sent verbatim; the caller debounces. */
    async searchCustomers(q, limit = 10) {
        const qs = new URLSearchParams();
        qs.set("q", q);
        qs.set("limit", String(limit));
        return this.request("GET", `/pos/v1/customers?${qs.toString()}`);
    }
    /** PATCH /pos/v1/customers/{id} — explicitly update a customer's name
     *  and/or email. Unlike order-create backfill, this overwrites populated
     *  fields so the cashier can correct misspellings. */
    async updateCustomer(id, body) {
        await this.request("PATCH", `/pos/v1/customers/${encodeURIComponent(id)}`, { body });
    }
    /** GET /pos/v1/locations/{id}/inventory — dealer-inventory list
     *  for the location. After V0070 the server returns the FULL
     *  current snapshot (cached per (location, provider) in S3,
     *  refreshed at most once per location-local calendar day or on
     *  the explicit Refresh CTA). The client filters / sorts /
     *  paginates locally — no more wire pagination.
     *
     *  Option B: when the server returns ``items_url`` (cache hit),
     *  the SDK fetches the inventory blob directly from S3 instead
     *  of waiting for the Lambda to stream it through. The returned
     *  envelope's ``items`` is replaced with what S3 served so the
     *  caller never has to know which path served them. */
    async listLocationInventory(locationId, params = {}) {
        const qs = new URLSearchParams();
        if (params.status != null)
            qs.set("status", params.status);
        const search = qs.toString();
        const envelope = await this.request("GET", `/pos/v1/locations/${encodeURIComponent(locationId)}/inventory${search ? `?${search}` : ""}`);
        return materializeInventoryItems(envelope);
    }
    /** POST /pos/v1/locations/{id}/inventory/refresh — explicit "Refresh
     *  inventory" CTA (V0070). Always bypasses the S3 cache: paginates
     *  the upstream provider to exhaustion, writes a fresh snapshot,
     *  returns it. Used by the Inventory page's Refresh button when
     *  the cashier knows an upstream change happened. */
    async refreshLocationInventory(locationId) {
        const envelope = await this.request("POST", `/pos/v1/locations/${encodeURIComponent(locationId)}/inventory/refresh`);
        // Refresh path always inlines ``items``, but materialize is a
        // no-op when ``items_url`` is missing — keep it through here so
        // any future code that returns a presigned URL on refresh
        // (e.g. for very large feeds) still flows correctly.
        return materializeInventoryItems(envelope);
    }
    /** GET /pos/v1/profile/device-preferences — per-location available
     *  devices + the cashier's current default for each (V0053 / #155).
     *  Drives the MethodPicker Card panel's terminal dropdown. */
    async getDevicePreferences() {
        return this.request("GET", "/pos/v1/profile/device-preferences");
    }
    /** GET /pos/v1/locations/{id}/payment-rails — DISTINCT enabled
     *  (payment_type, payment_subtype) tuples the V0021 cascade
     *  resolves at this location. MethodPicker uses this to gate
     *  tile ``available`` so unconfigured methods render in a "coming
     *  soon" state instead of letting cashiers tap a tile that 400s
     *  with ``no_binding`` (#155). */
    async getLocationPaymentRails(locationId) {
        return this.request("GET", `/pos/v1/locations/${encodeURIComponent(locationId)}/payment-rails`);
    }
    /** GET /pos/v1/locations/{id}/valor-virtual-devices — all Soft POS /
     *  virtual devices bound at this location (or its org / partner).
     *  The VirtualTerminalPanel uses this to render a device picker when
     *  there are 2+ devices; auto-selects silently when exactly one is
     *  returned. */
    async listVirtualDevices(locationId) {
        return this.request("GET", `/pos/v1/locations/${encodeURIComponent(locationId)}/valor-virtual-devices`);
    }
    /** PUT /pos/v1/profile/device-preferences — upsert the cashier's
     *  default for one (location, card_present) tuple. */
    async putDevicePreference(body) {
        await this.request("PUT", "/pos/v1/profile/device-preferences", { body });
    }
    /** GET /pos/v1/orders/{id} — single order with payment timeline. */
    async getOrder(orderId) {
        return this.request("GET", `/pos/v1/orders/${encodeURIComponent(orderId)}`);
    }
    /** PATCH /pos/v1/orders/{id} — edit reference / customer / amount /
     *  etc on an open order (#152). Editing partially-paid + paid orders
     *  needs the supervisor PIN gate that lands with #149; until then
     *  the API 409s those statuses. Voided + refunded orders are
     *  terminal — never editable. */
    async patchOrder(orderId, body) {
        return this.request("PATCH", `/pos/v1/orders/${encodeURIComponent(orderId)}`, {
            body,
        });
    }
    /** GET /pos/v1/payments/pending — flat list of PENDING/ACTION_REQUIRED charge payments. */
    async listPendingPayments(params = {}) {
        const qs = new URLSearchParams();
        if (params.location_ids && params.location_ids.length > 0) {
            qs.set("location_ids", params.location_ids.join(","));
        }
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/pos/v1/payments/pending${suffix}`);
    }
    /** GET /pos/v1/orders — filtered, paginated list. */
    async listOrders(params = {}) {
        const qs = new URLSearchParams();
        if (params.limit != null)
            qs.set("limit", String(params.limit));
        if (params.offset != null)
            qs.set("offset", String(params.offset));
        if (params.status)
            qs.set("status", params.status);
        if (params.q)
            qs.set("q", params.q);
        if (params.date_from)
            qs.set("date_from", params.date_from);
        if (params.date_to)
            qs.set("date_to", params.date_to);
        if (params.sort_by)
            qs.set("sort_by", params.sort_by);
        if (params.sort_dir)
            qs.set("sort_dir", params.sort_dir);
        if (params.department_id)
            qs.set("department_id", params.department_id);
        if (params.payment_type)
            qs.set("payment_type", params.payment_type);
        if (params.exceptions_only)
            qs.set("exceptions_only", "true");
        if (params.discount_only)
            qs.set("discount_only", "true");
        if (params.location_ids && params.location_ids.length > 0) {
            qs.set("location_ids", params.location_ids.join(","));
        }
        if (params.channel)
            qs.set("channel", params.channel);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/pos/v1/orders${suffix}`);
    }
    /** POST /pos/v1/orders/{id}/payments — initiate cash/check. */
    async createPayment(orderId, body) {
        return this.request("POST", `/pos/v1/orders/${encodeURIComponent(orderId)}/payments`, { body });
    }
    /** POST /pos/v1/orders/{id}/payments/{payment_id}/cancel — cancel a
     *  non-terminal pending payment. Server returns 409 if the payment
     *  is already terminal. The poller continues to watch the row in
     *  the watch window so a vendor late-completion still surfaces in
     *  the lifecycle audit. */
    async cancelPayment(orderId, paymentId, body) {
        return this.request("POST", `/pos/v1/orders/${encodeURIComponent(orderId)}/payments/${encodeURIComponent(paymentId)}/cancel`, { body });
    }
    /** POST /pos/v1/orders/{id}/discounts — apply a cashier discount to
     *  an open order. Server validates the type, enforces the cap, and
     *  auto-closes at 100%. Returns the updated order envelope. */
    async applyOrderDiscount(orderId, body) {
        return this.request("POST", `/pos/v1/orders/${encodeURIComponent(orderId)}/discounts`, { body });
    }
    /** POST /pos/v1/orders/{id}/discounts/{discountId}/void — soft-delete
     *  a previously applied discount. Recomputes the order discount cache
     *  and reopens a 100%-closed order if applicable. */
    async voidOrderDiscount(orderId, discountId, body) {
        return this.request("POST", `/pos/v1/orders/${encodeURIComponent(orderId)}/discounts/${encodeURIComponent(discountId)}/void`, { body });
    }
    /** GET /pos/v1/orders/{id}/payments/{payment_id}/lifecycle — surface
     *  every event written to payment_lifecycle_event_tbl (V0056) for
     *  one payment. The Timeline UI's collapsible "Show history" toggle
     *  pulls this lazily so the cashier never sees clutter on the happy
     *  path but can drill in when something looks off. */
    async getPaymentLifecycle(orderId, paymentId) {
        return this.request("GET", `/pos/v1/orders/${encodeURIComponent(orderId)}/payments/${encodeURIComponent(paymentId)}/lifecycle`);
    }
    /** POST /pos/v1/orders/{id}/refunds — refund a previous APPROVED
     *  cash / check payment on this order. Card / ACH refunds dispatch
     *  through the provider plugin once #130 lands. */
    async refundPayment(orderId, body) {
        return this.request("POST", `/pos/v1/orders/${encodeURIComponent(orderId)}/refunds`, { body });
    }
    /** GET /pos/v1/orders/{id}/payments/{payment_id}/settlement-status
     *  Auto-determines whether the payment is void-eligible (not yet settled)
     *  or must be refunded (already in a closed batch). V1 parity:
     *  payment_get_settlement_status + RefundModal.vue isVoidOperation. */
    async getPaymentSettlementStatus(orderId, paymentId) {
        return this.request("GET", `/pos/v1/orders/${encodeURIComponent(orderId)}/payments/${encodeURIComponent(paymentId)}/settlement-status`);
    }
    /** POST /pos/v1/orders/{id}/void — void an open order with no payments. */
    async voidOrder(orderId) {
        return this.request("POST", `/pos/v1/orders/${encodeURIComponent(orderId)}/void`);
    }
    /** POST /pos/v1/locations/{id}/iframe-token — mint a short-lived
     *  hosted-iframe client token for the location's bound card
     *  provider (Valor today). Returns the token + an ISO-8601
     *  ``expires_at`` so the FE can refresh before the iframe times
     *  out. ``payment_subtype`` defaults to ``"virtual"`` (the most
     *  common hosted-UI use case); ``card_present`` / ``pay_by_text``
     *  also accepted. */
    async mintIframeToken(locationId, body = {}) {
        return this.request("POST", `/pos/v1/locations/${encodeURIComponent(locationId)}/iframe-token`, { body });
    }
    /** GET /pos/v1/reports/end-of-day — counts + gross + refunds + net
     *  per method + per cashier for the given date / location. Cashier
     *  with view_own gets a self-only view; supervisor / manager sees
     *  the whole location. */
    async getEndOfDayReport(params) {
        const qs = new URLSearchParams();
        qs.set("date", params.date);
        if (params.location_id)
            qs.set("location_id", params.location_id);
        if (params.cashier_membership_id)
            qs.set("cashier_membership_id", params.cashier_membership_id);
        return this.request("GET", `/pos/v1/reports/end-of-day?${qs.toString()}`);
    }
    /** GET /pos/v1/reports/batch-out — V1-parity Reports payload:
     *  settlement batches + by_payment_type + by_department rollups +
     *  per-payment detail rows + KPI totals. (#148) */
    async getBatchOutReport(params) {
        const qs = new URLSearchParams();
        qs.set("date_from", params.date_from);
        qs.set("date_to", params.date_to);
        if (params.location_id)
            qs.set("location_id", params.location_id);
        if (params.location_ids && params.location_ids.length > 0) {
            // Backend accepts a comma-separated UUID list (max 256 entries).
            // Single-element arrays are equivalent to passing ``location_id``;
            // we forward as ``location_ids`` either way so the backend can
            // emit one consistent multi-vs-legacy resolution path.
            qs.set("location_ids", params.location_ids.join(","));
        }
        if (params.period)
            qs.set("period", params.period);
        return this.request("GET", `/pos/v1/reports/batch-out?${qs.toString()}`);
    }
    /** GET /pos/v1/orders/{id}/receipt — render JSON receipt content.
     *  UI templates the actual print/email/SMS form. */
    async getReceipt(orderId) {
        return this.request("GET", `/pos/v1/orders/${encodeURIComponent(orderId)}/receipt`);
    }
    /** GET /pos/v1/orders/{id}/receipts — list all stored receipt artifacts.
     *  source='provider' entries are processor-generated (e.g. Valor cc receipt). */
    async listReceiptArtifacts(orderId) {
        return this.request("GET", `/pos/v1/orders/${encodeURIComponent(orderId)}/receipts`);
    }
    /** Returns the authenticated URL to fetch a receipt artifact as HTML.
     *  target is a payment UUID or the literal string "order". */
    receiptHtmlUrl(orderId, kind, target) {
        return `${this.baseUrl}/pos/v1/orders/${encodeURIComponent(orderId)}/receipts/${encodeURIComponent(kind)}/${encodeURIComponent(target)}`;
    }
    async request(method, path, opts = {}) {
        const { authenticated = true, body } = opts;
        const headers = {
            "content-type": "application/json",
        };
        if (authenticated) {
            const token = await this.tokens.getAccessToken();
            if (!token) {
                await this.tokens.onUnauthorized?.();
                throw new ApiError(401, { error: "unauthenticated" });
            }
            headers.authorization = `Bearer ${token}`;
            if (this.getActiveMembershipId) {
                const id = await this.getActiveMembershipId();
                if (id) {
                    headers["x-active-membership-id"] = id;
                }
            }
        }
        const init = { method, headers };
        if (body !== undefined) {
            init.body = JSON.stringify(body);
        }
        const response = await this.fetchImpl(`${this.baseUrl}${path}`, init);
        const text = await response.text();
        let parsed;
        try {
            parsed = text.length > 0 ? JSON.parse(text) : undefined;
        }
        catch {
            parsed = text;
        }
        if (!response.ok) {
            if (response.status === 401) {
                await this.tokens.onUnauthorized?.();
            }
            throw new ApiError(response.status, parsed);
        }
        return parsed;
    }
    /** POST /pos/v1/orders/{id}/kiosk-handoff
     *
     * Mints a 30-minute signed handoff token so the cashier can hand off an
     * open order to a kiosk for customer self-service payment (D12).
     *
     * Returns a {handoff_url, qr_payload, expires_at} envelope. The caller
     * renders a QR from `qr_payload` (same value as `handoff_url`). The
     * customer scans it at the kiosk to claim the order and complete payment.
     */
    async createKioskHandoff(orderId) {
        return this.request("POST", `/pos/v1/orders/${encodeURIComponent(orderId)}/kiosk-handoff`);
    }
}
/** Option B — when the inventory list response carries an
 *  ``items_url``, fetch the blob directly from S3 (CORS-allowed
 *  on the inventory-cache bucket) and merge into the envelope.
 *  No-op when ``items_url`` is missing or empty (live-refresh /
 *  DB-fallback paths still inline ``items`` server-side).
 *
 *  Failure mode: if the S3 fetch fails (network blip, signature
 *  expiry, bucket misconfig), we fall back to the inline ``items``
 *  array on the envelope — which is empty on the cache-hit path,
 *  so the caller sees an empty inventory until next refresh.
 *  Acceptable: the page surfaces a banner, the cashier hits
 *  Refresh, and the live-refresh path inlines items the
 *  traditional way. */
async function materializeInventoryItems(envelope) {
    const url = envelope.items_url;
    if (!url)
        return envelope;
    try {
        const resp = await fetch(url);
        if (!resp.ok) {
            throw new Error(`S3 returned ${resp.status} ${resp.statusText}`);
        }
        const blob = await resp.json();
        const items = Array.isArray(blob.items) ? blob.items : [];
        return { ...envelope, items };
    }
    catch (err) {
        // Surface the failure to the caller via console — Sentry / the
        // Integration Health surface will pick this up once those land
        // on the cashier-side. Fall through with the inline ``items``
        // (empty on cache hit, full on live/DB) so the page still has
        // something to render.
        if (typeof console !== "undefined" && typeof console.warn === "function") {
            console.warn("inventory items_url fetch failed; falling back to inline items", err);
        }
        return envelope;
    }
}
//# sourceMappingURL=client.js.map